import { defineStore, storeToRefs } from "pinia";
import { ref, computed, watch } from "vue";
import { deepCopy, uniq } from "@utils/toolBox";
import useMyFetch from "@stores/useMyFetch";
import { getClientId } from "@utils/clientId";
import handleError from "@composables/handleError.js";
import { useSurveyStore } from "@stores/survey/survey";
import { getRegionOptions } from "@/utils/constants/regions";
import { useMailingListsStore } from "@stores/survey/mailing/mailingLists";
import { NotFoundError, ForbiddenError, UnauthorizedError } from "@routes/errors";
import { getLanguageOptions as getLocaleOptions } from "@/utils/constants/languages";
import { captureEvent } from "@/lib/analytics";

export const useEmailCampaignStore = defineStore("emailCampaign", () => {
  // item
  const emailCampaign = ref({});
  const initialEmailCampaign = ref({});
  const isNewCampaign = ref(false);

  // stores
  const { token, survey } = storeToRefs(useSurveyStore());
  const { lightMailingLists } = storeToRefs(useMailingListsStore());

  const hasCampaignChanged = computed(() => {
    const editableFields = [
      "name",
      "delivery_mode",
      "subject",
      "mailing_list_id",
      "country",
      "language",
      "template_id",
      "url_params",
      "scheduled_at",
      "conditions",
      "sender_name",
    ];
    return editableFields.some((field) => {
      return JSON.stringify(emailCampaign.value[field]) !== JSON.stringify(initialEmailCampaign.value[field]);
    });
  });

  const isCampaignEditable = computed(() => ["draft", "suspended", "error"].includes(emailCampaign.value?.status));
  const isTransactionalCampaign = computed(() => emailCampaign.value?.delivery_mode === "transactional");
  const isBatchCampaign = computed(() => emailCampaign.value?.delivery_mode === "batch");
  const isSuspendedCampaign = computed(() => emailCampaign.value?.status === "suspended");
  const hasScheduledDate = computed(() => emailCampaign.value?.scheduled_at != null);
  const getCurrentCampaignSurveyId = computed(() => emailCampaign.value?.survey_id);
  const getCurrentCampaignMailingListId = computed(() => emailCampaign.value?.mailing_list_id);
  const getEmailCampaign = async (campaignToken) => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/email_campaigns/${campaignToken}`).json();
      if (response.value.status === 404) throw new NotFoundError("Page not found");
      if (response.value.status === 403) throw new ForbiddenError("User not allowed");
      if (response.value.status === 401) throw new UnauthorizedError("User not logged in");
      if (response.value.ok) {
        emailCampaign.value = data.value;
        initialEmailCampaign.value = deepCopy(data.value);
        isNewCampaign.value = false;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const initEmailCampaign = () => {
    const defaultCountry = getCountryOptions.value.length === 1 ? getCountryOptions.value[0]?.value : null;
    const defaultLanguage = getLanguageOptions.value.length === 1 ? getLanguageOptions.value[0]?.value : null;
    emailCampaign.value = {
      status: "draft",
      delivery_mode: "batch",
      conditions: [],
      url_params: [],
      country: defaultCountry,
      language: defaultLanguage,
    };
    isNewCampaign.value = true;
  };

  const createEmailCampaign = async () => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/email_campaigns`)
        .json()
        .post(emailCampaign, null);

      if (response.value.ok) {
        emailCampaign.value = data.value;
        initialEmailCampaign.value = deepCopy(data.value);
        isNewCampaign.value = false;

        captureEvent("email_campaign_created");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const updateEmailCampaign = async () => {
    try {
      const { response, data } = await useMyFetch(
        `/surveys/${token.value}/email_campaigns/${emailCampaign.value.token}`,
      )
        .json()
        .patch(emailCampaign, null);

      if (response.value.ok) {
        emailCampaign.value = data.value;
        initialEmailCampaign.value = deepCopy(data.value);

        captureEvent("email_campaign_updated");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const scheduleEmailCampaign = async () => {
    try {
      const { response, data } = await useMyFetch(
        `/surveys/${token.value}/email_campaigns/${emailCampaign.value.token}/schedule`,
      )
        .post({ client_id: getClientId() })
        .json();

      if (response.value.ok) {
        emailCampaign.value = data.value;
        initialEmailCampaign.value = deepCopy(data.value);

        captureEvent("email_campaign_scheduled");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const testEmailCampaign = async (testContact = null) => {
    let testUrl = `/surveys/${token.value}/email_campaigns/${emailCampaign.value.token}/test`;
    if (testContact != null) testUrl += `?test_contact=${testContact}`;
    try {
      const { response, data } = await useMyFetch(testUrl).post({ client_id: getClientId() }).json();
      if (response.value.ok) {
        emailCampaign.value = data.value;
        initialEmailCampaign.value = deepCopy(data.value);

        captureEvent("email_campaign_test_sent");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const pushEmailCampaign = async () => {
    try {
      const { response, data } = await useMyFetch(
        `/surveys/${token.value}/email_campaigns/${emailCampaign.value.token}/push`,
      )
        .post({ client_id: getClientId() })
        .json();
      if (response.value.ok) {
        emailCampaign.value = data.value;
        initialEmailCampaign.value = deepCopy(data.value);

        captureEvent("email_campaign_pushed");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const estimateEmailCampaignAudience = async () => {
    try {
      const { response, data } = await useMyFetch(
        `/surveys/${token.value}/email_campaigns/${emailCampaign.value.token}/estimate_audience`,
      )
        .patch({ conditions: emailCampaign.value.conditions, mailing_list_id: emailCampaign.value.mailing_list_id })
        .json();
      if (response.value.ok) {
        emailCampaign.value.statistics = { targeted: data.value.audience };
        initialEmailCampaign.value.statistics = deepCopy({ targeted: data.value.audience });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const getCountryOptions = computed(() => {
    const regions = getRegionOptions();
    const campaignSurvey = survey.value;
    if (!campaignSurvey) return [];

    const targetountries = uniq(campaignSurvey?.targets.map((t) => t.countries).flat());
    return regions.filter((r) => targetountries.includes(r.value));
  });
  const getLanguageOptions = computed(() => {
    const languages = Object.values(getLocaleOptions());
    const campaignSurvey = survey.value;
    if (!campaignSurvey) return [];

    const localeLanguages = Object.values(campaignSurvey?.locale_by_ids).map((l) => l.language);
    return languages.filter((r) => localeLanguages.includes(r.value));
  });

  // url params
  const getCurrentCampaignUrlParams = computed(() => emailCampaign.value?.url_params ?? defineCampaignUrlParams());
  const defineCampaignUrlParams = () => (emailCampaign.value.url_params = []);
  const addCampaignUrlParam = () => getCurrentCampaignUrlParams.value.push({ name: "", value: "" });
  const removeCampaignUrlParam = (index) => getCurrentCampaignUrlParams.value.splice(index, 1);

  // conditions
  const campaignListAttributes = ref({});
  const fetchListAttributes = () => {
    const mailingList = lightMailingLists.value.find((list) => list.id === getCurrentCampaignMailingListId.value);
    campaignListAttributes.value = mailingList?.contact_attributes ?? {};
  };
  const computeOptions = (options) => options?.map((key) => ({ label: key, value: key } || []));
  const getListAttributeKeyOptions = computed(() => computeOptions(Object.keys(campaignListAttributes.value)));
  const getListAttributeValueOptions = (name) => computeOptions(campaignListAttributes.value?.[name]);

  // survey related params
  const resetSurveyRelatedParams = () => {
    emailCampaign.value.country = "";
    emailCampaign.value.language = "";
  };

  watch(getCurrentCampaignSurveyId, (oldData, newData) => {
    if (!newData) return;
    if (oldData !== newData) resetSurveyRelatedParams();
  });

  watch(getCurrentCampaignMailingListId, () => fetchListAttributes());

  return {
    // item
    emailCampaign,
    initialEmailCampaign,
    isNewCampaign,
    isTransactionalCampaign,
    isBatchCampaign,
    isSuspendedCampaign,
    hasScheduledDate,
    hasCampaignChanged,
    isCampaignEditable,
    getCountryOptions,
    getLanguageOptions,
    getEmailCampaign,
    initEmailCampaign,
    createEmailCampaign,
    updateEmailCampaign,
    scheduleEmailCampaign,
    testEmailCampaign,
    pushEmailCampaign,
    estimateEmailCampaignAudience,
    // url params
    getCurrentCampaignUrlParams,
    addCampaignUrlParam,
    removeCampaignUrlParam,
    // conditions
    campaignListAttributes,
    getListAttributeKeyOptions,
    getListAttributeValueOptions,
    fetchListAttributes,
  };
});
